import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Tabs,
  Tab,
  IconButton,
  ListItemSecondaryAction,
  CardContent
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import {
  DELETE_CLIENT_EMPLOYEES_JOBS,
  DELETE_CLIENT_JOB_ACTIVITIES,
  FETCH_CLIENT_EMPLOYEES,
  FETCH_CLIENT_EMPLOYEES_JOBS,
  FETCH_CLIENT_JOB_ACTIVITIES,
  FETCH_CLIENT_JOBS_LIST,
  SAVE_CLIENT_EMPLOYEES_JOBS,
  SAVE_CLIENT_JOB_ACTIVITIES,
  SAVE_CLIENT_JOBS
} from "../../constant/Apipath";
import { showErrorToast, showSuccessToast } from "../../toast/toast";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import PlaceIcon from "@mui/icons-material/Place";
import GroupIcon from "@mui/icons-material/Group";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WorkIcon from "@mui/icons-material/Work";
import SearchIcon from "@mui/icons-material/Search";
import SearchMap from "../../user/sites/SearchMap";
import { styled } from "@mui/material/styles";

const jobTablePageSize = 50;
const activityPageSize = 50;

const initialNewJob = {
  jobName: "",
  jobCode: "",
  active: true,
  address1: "",
  address2: "",
  city: "",
  province: "",
  country: "",
  postalCode: ""
};

const ToggleSwitch = styled(Switch)(({ theme }) => ({
  width: 52,
  height: 30,
  padding: 0,
  display: "flex",
  alignItems: "center",
  "& .MuiSwitch-switchBase": {
    padding: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#1E88E5",
        opacity: 1
      }
    }
  },
  "& .MuiSwitch-thumb": {
    width: 26,
    height: 26,
    boxShadow: "none"
  },
  "& .MuiSwitch-track": {
    borderRadius: 30,
    backgroundColor: "#CFD8DC",
    opacity: 1
  }
}));

const getAddressComponent = (components, type, useShort = false) => {
  const component = components?.find((item) => item.types.includes(type));
  if (!component) return "";
  return useShort ? component.short_name : component.long_name;
};

const getStreetDetail = (components) => {
  const number = getAddressComponent(components, "street_number");
  const route = getAddressComponent(components, "route");
  const unit = getAddressComponent(components, "subpremise");
  const parts = [number, route].filter(Boolean);
  let detail = parts.join(" ").trim();
  if (!detail) {
    detail =
      getAddressComponent(components, "premise") ||
      getAddressComponent(components, "locality") ||
      getAddressComponent(components, "administrative_area_level_2");
  }
  if (unit) {
    detail = `${detail} #${unit}`.trim();
  }
  return detail || "";
};

function Jobs() {
  const { loginData, selectedClientData } = useSelector((state) => state?.main || {});
  const theme = useTheme();
  const clientId = selectedClientData?.ClientId;
  const accessToken = loginData?.accessToken;
  const loginUserId = loginData?.userInformation?.UserId;

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailLoading, setJobDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0: Details, 1: Activities, 2: Employees
  const [jobPage, setJobPage] = useState(1);
  const [jobHasMore, setJobHasMore] = useState(true);
  const [loadingMoreJobs, setLoadingMoreJobs] = useState(false);
  const jobListRef = useRef(null);
  const selectedJobRef = useRef(null);
  const manualSelectedJobId = useRef(null);
  const jobsRef = useRef([]);
  const [jobSearchTerm, setJobSearchTerm] = useState("");
  const [debouncedJobSearchTerm, setDebouncedJobSearchTerm] = useState("");

  // Activities State
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityHasMore, setActivityHasMore] = useState(true);
  const [loadingMoreActivities, setLoadingMoreActivities] = useState(false);
  const [activitySearchTerm, setActivitySearchTerm] = useState("");
  const [debouncedActivitySearchTerm, setDebouncedActivitySearchTerm] = useState("");
  const activityListRef = useRef(null);
  const [activityForm, setActivityForm] = useState({ id: "", name: "" });
  const [activitySaving, setActivitySaving] = useState(false);
  const [activityDeletingId, setActivityDeletingId] = useState(null);

  // Assignments State
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [assignmentHasMore, setAssignmentHasMore] = useState(true);
  const [loadingMoreAssignments, setLoadingMoreAssignments] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [debouncedEmployeeSearchTerm, setDebouncedEmployeeSearchTerm] = useState("");
  const assignmentListRef = useRef(null);
  const [togglingEmployeeId, setTogglingEmployeeId] = useState(null);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedActivitySearchTerm(activitySearchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [activitySearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedEmployeeSearchTerm(employeeSearchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [employeeSearchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedJobSearchTerm(jobSearchTerm.trim());
    }, 400);
    return () => clearTimeout(handler);
  }, [jobSearchTerm]);

  // Add Job State
  const [addJobOpen, setAddJobOpen] = useState(false);
  const [newJobForm, setNewJobForm] = useState(initialNewJob);
  const [savingJob, setSavingJob] = useState(false);
  const [addressSearchTerm, setAddressSearchTerm] = useState("");
  const resetJobForm = useCallback(() => {
    setNewJobForm(initialNewJob);
    setAddressSearchTerm("");
  }, []);

  const getJobId = useCallback(
    (job) => job?.clientJobId || job?.id || job?.ClientJobId || job?.JobId,
    []
  );

  // --- API CALLS ---

  const buildJobsPayload = useCallback((page = 1, searchText = "") => ({
    PageNr: page,
    NrOfRecPerPage: jobTablePageSize,
    FullSearch: searchText,
    SearchList: [{ ClientId: clientId, deleted: false }],
    SortList: [{ FieldName: "id", Direction: "DESC" }],
    IncludeRecordNr: true,
    ReturnRecordError: true
  }), [clientId]);

  const fetchJobDetail = useCallback(async (jobId) => {
    if (!jobId || !clientId) return null;
    setJobDetailLoading(true);
    try {
      const payload = {
        PageNr: 1, NrOfRecPerPage: 1, FullSearch: "",
        SearchList: [{ ClientId: clientId, clientJobId: jobId, deleted: false }],
        SortList: [{ FieldName: "id", Direction: "DESC" }], IncludeRecordNr: true, ReturnRecordError: true
      };
      const response = await Postrequestcall(FETCH_CLIENT_JOBS_LIST, payload, accessToken);
      if (response?.status === 200) {
        const detail = response?.data?.data?.[0];
        if (detail) {
          setSelectedJob(detail);
          selectedJobRef.current = detail;
        }
      }
    } catch (err) {
      showErrorToast("Unable to load job details.");
    } finally {
      setJobDetailLoading(false);
    }
  }, [accessToken, clientId]);

  /**
   * Fetches the list of activities assigned to the specific job.
   */
  const fetchJobActivities = useCallback(
    async (jobId, page = 1, append = false, searchTerm = "") => {
      if (!jobId || !clientId) return;
      if (append) {
        setLoadingMoreActivities(true);
      } else {
        setActivityLoading(true);
      }
      try {
        const payload = {
          PageNr: page,
          NrOfRecPerPage: activityPageSize,
          FullSearch: searchTerm,
          SearchList: [{ JobId: jobId, ClientId: clientId, deleted: false }],
          SortList: [{ FieldName: "id", Direction: "DESC" }],
          IncludeRecordNr: true,
          ReturnRecordError: true
        };
        const response = await Postrequestcall(FETCH_CLIENT_JOB_ACTIVITIES, payload, accessToken);
        if (response?.status === 200) {
          const list = response?.data?.data || [];
          const totalRecords = response?.data?.nrOfRecords ?? response?.data?.RecordNr ?? list.length;
          if (append) {
            setActivities((prev) => {
              const next = [...prev, ...list];
              setActivityHasMore(next.length < totalRecords);
              return next;
            });
          } else {
            setActivities(list);
            setActivityHasMore(list.length < totalRecords);
          }
          setActivityPage(page);
        }
      } catch (err) {
        showErrorToast("Unable to load activities.");
      } finally {
        if (append) {
          setLoadingMoreActivities(false);
        } else {
          setActivityLoading(false);
        }
      }
    },
    [accessToken, clientId]
  );

  useEffect(() => {
    if (!selectedJob) return;
    const jobId = getJobId(selectedJob);
    setActivityPage(1);
    setActivityHasMore(true);
    fetchJobActivities(jobId, 1, false, debouncedActivitySearchTerm);
  }, [selectedJob, debouncedActivitySearchTerm, fetchJobActivities, getJobId]);

  const fetchJobAssignments = useCallback(
    async (jobId, page = 1, append = false, searchTerm = "") => {
      if (!jobId || !clientId) return;
      if (append) {
        setLoadingMoreAssignments(true);
      } else {
        setAssignmentLoading(true);
      }
      try {
        const payload = {
          PageNr: page,
          NrOfRecPerPage: 50,
          FullSearch: searchTerm,
          SearchList: [{ JobId: jobId, ClientId: clientId, deleted: false }],
          SortList: [{ FieldName: "isSelected", Direction: "DESC" }],
          IncludeRecordNr: true,
          ReturnRecordError: true
        };
        const response = await Postrequestcall(FETCH_CLIENT_EMPLOYEES_JOBS, payload, accessToken);
        if (response?.status === 200) {
          const list = response?.data?.data || [];
          const totalRecords = response?.data?.nrOfRecords ?? response?.data?.RecordNr ?? list.length;
          if (append) {
            setAssignedEmployees((prev) => {
              const next = [...prev, ...list];
              setAssignmentHasMore(next.length < totalRecords);
              return next;
            });
          } else {
            setAssignedEmployees(list);
            setAssignmentHasMore(list.length < totalRecords);
          }
          setAssignmentPage(page);
        }
      } catch (err) {
        showErrorToast("Unable to load assignments.");
      } finally {
        if (append) {
          setLoadingMoreAssignments(false);
        } else {
          setAssignmentLoading(false);
        }
      }
    },
    [accessToken, clientId]
  );

  useEffect(() => {
    if (!selectedJob) return;
    const jobId = getJobId(selectedJob);
    setAssignmentPage(1);
    setAssignmentHasMore(true);
    fetchJobAssignments(jobId, 1, false, debouncedEmployeeSearchTerm);
  }, [selectedJob, debouncedEmployeeSearchTerm, fetchJobAssignments, getJobId]);

  const selectedJobId = getJobId(selectedJob);

  useEffect(() => {
    if (!selectedJobId) return;
    // fetchJobDetail(selectedJobId);
  }, [selectedJobId]);

  const handleSelectJob = useCallback(
    async (job) => {
      const jobId = getJobId(job);
      if (!jobId) return;
      manualSelectedJobId.current = jobId;
      setSelectedJob(job);
      selectedJobRef.current = job;
      setActiveTab(0);
      setActivities([]);
      setActivityPage(1);
      setActivityHasMore(true);
      setAssignedEmployees([]);
      setAssignmentPage(1);
      setAssignmentHasMore(true);
    },
    [getJobId]
  );

  const fetchJobs = useCallback(
    async (page = 1, append = false, searchText = "", preferredJobId = null) => {
      if (!clientId) {
        setJobs([]);
        jobsRef.current = [];
        return;
      }
      if (append) {
        setLoadingMoreJobs(true);
      } else {
        setLoadingJobs(true);
      }
      try {
        const response = await Postrequestcall(
          FETCH_CLIENT_JOBS_LIST,
          buildJobsPayload(page, searchText),
          accessToken
        );
        if (response?.status === 200) {
          const jobList = response?.data?.data || [];
          const totalRecords =
            response?.data?.nrOfRecords ?? response?.data?.RecordNr ?? jobList.length;
          const nextList = append ? [...jobsRef.current, ...jobList] : jobList;
          setJobs(nextList);
          jobsRef.current = nextList;
          setJobHasMore(nextList.length < totalRecords);
          setJobPage(page);

          const existingJobId = selectedJobRef.current ? getJobId(selectedJobRef.current) : null;
          const targetJobId = preferredJobId || manualSelectedJobId.current || existingJobId;
          let jobToSelect = null;
          if (targetJobId) {
            jobToSelect = nextList.find((item) => getJobId(item) === targetJobId);
          } else if (!append && nextList.length > 0) {
            jobToSelect = nextList[0];
          }

          if (jobToSelect) {
            const selectedJobId = getJobId(jobToSelect);
            if (manualSelectedJobId.current && manualSelectedJobId.current === selectedJobId) {
              manualSelectedJobId.current = null;
            }
            const shouldReselect =
              !selectedJobRef.current || selectedJobId !== existingJobId;
            if (shouldReselect) {
              handleSelectJob(jobToSelect);
            }
          } else {
            setSelectedJob(null);
            selectedJobRef.current = null;
          }
        }
      } catch (err) {
        showErrorToast("Unable to fetch jobs.");
      } finally {
        if (append) {
          setLoadingMoreJobs(false);
        } else {
          setLoadingJobs(false);
        }
      }
    },
    [accessToken, buildJobsPayload, clientId, handleSelectJob, getJobId]
  );

  useEffect(() => {
    if (!clientId) {
      setJobs([]);
      jobsRef.current = [];
      setJobHasMore(true);
      setJobPage(1);
      return;
    }
    manualSelectedJobId.current = null;
    setJobHasMore(true);
    setJobPage(1);
    fetchJobs(1, false, debouncedJobSearchTerm);
    jobListRef.current?.scrollTo?.(0, 0);
  }, [clientId, debouncedJobSearchTerm, fetchJobs]);

  const handleJobListScroll = useCallback(
    (event) => {
      const target = event.currentTarget;
      if (!target || !jobHasMore || loadingJobs || loadingMoreJobs) return;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
      if (nearBottom) {
        fetchJobs(jobPage + 1, true, debouncedJobSearchTerm);
      }
    },
    [jobHasMore, loadingJobs, loadingMoreJobs, fetchJobs, jobPage, debouncedJobSearchTerm]
  );

  const handleActivityScroll = useCallback(
    (event) => {
      const target = event.currentTarget;
      if (
        !target ||
        !activityHasMore ||
        activityLoading ||
        loadingMoreActivities ||
        !selectedJob
      )
        return;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
      if (nearBottom) {
        const jobId = getJobId(selectedJob);
        if (jobId) {
          fetchJobActivities(jobId, activityPage + 1, true);
        }
      }
    },
    [
      activityHasMore,
      activityLoading,
      loadingMoreActivities,
      activityPage,
      fetchJobActivities,
      getJobId,
      selectedJob
    ]
  );

  const handleAssignmentScroll = useCallback(
    (event) => {
      const target = event.currentTarget;
      if (
        !target ||
        !selectedJob ||
        !assignmentHasMore ||
        assignmentLoading ||
        loadingMoreAssignments
      )
        return;
      const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
      if (nearBottom) {
        const jobId = getJobId(selectedJob);
        if (jobId) {
          fetchJobAssignments(jobId, assignmentPage + 1, true);
        }
      }
    },
    [
      assignmentHasMore,
      assignmentLoading,
      loadingMoreAssignments,
      assignmentPage,
      fetchJobAssignments,
      getJobId,
      selectedJob
    ]
  );

  // --- HANDLERS ---

  const handleJobFormChange = (field, value) => {
    setNewJobForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateJob = async () => {
    if (!newJobForm.jobName || !newJobForm.jobCode) {
      showErrorToast("Name and Code required");
      return;
    }
    setSavingJob(true);
    try {
      const payload = {
        SaveList: [{
          Id: 0, ClientId: clientId, JobCode: newJobForm.jobCode, Name: newJobForm.jobName,
          Address1: newJobForm.address1, Address2: newJobForm.address2, City: newJobForm.city,
          Province: newJobForm.province, Country: newJobForm.country, PostalCode: newJobForm.postalCode,
          Latitude: 0, Longitude: 0, Active: newJobForm.active, ModifiedBy: loginUserId
        }],
        ReturnRecordError: true, UserId: loginUserId
      };
      const response = await Postrequestcall(SAVE_CLIENT_JOBS, payload, accessToken);
      if (response?.status === 200 || response?.status === 201) {
        showSuccessToast("Job created.");
        setAddJobOpen(false);
        resetJobForm();
        fetchJobs();
      } else showErrorToast("Failed to create job.");
    } catch (err) {
      showErrorToast("Error creating job.");
    } finally {
      setSavingJob(false);
    }
  };

  const handleJobAddressSelection = useCallback(
    (place) => {
      if (!place) return;
      const components = place?.address_components || [];
      const streetDetail = getStreetDetail(components) || place?.formatted_address || "";
      const city =
        getAddressComponent(components, "locality") ||
        getAddressComponent(components, "administrative_area_level_2");
      const province = getAddressComponent(components, "administrative_area_level_1");
      const postalCode = getAddressComponent(components, "postal_code");
      const country = getAddressComponent(components, "country");
      setAddressSearchTerm(place?.formatted_address || streetDetail);
      setNewJobForm((prev) => ({
        ...prev,
        address1: streetDetail,
        city,
        province,
        postalCode,
        country
      }));
    },
    []
  );

  /**
   * Toggles employee assignment.
   * ON: Calls SAVE_CLIENT_EMPLOYEES_JOBS to assign.
   * OFF: Calls DELETE_CLIENT_EMPLOYEES_JOBS to unassign.
   */
  const handleToggleAssignment = async (employee) => {
    const empId = employee?.clientEmployeeId || employee?.id;
    const jobId = getJobId(selectedJob);
    const isCurrentlyAssigned = Boolean(employee?.isSelected);

    setTogglingEmployeeId(empId);

    try {
      if (isCurrentlyAssigned) {
        // UNASSIGN
        const assignmentId = employee?.clientEmployeeJobId ?? employee?.id;
        if (!assignmentId) {
          showErrorToast("Unable to unassign employee.");
          return;
        }
        const payload = {
          UserId: loginUserId,
          ReturnRecordError: true,
          SoftDelete: true,
          DeleteList: [{ Id: assignmentId }]
        };
        const res = await Postrequestcall(DELETE_CLIENT_EMPLOYEES_JOBS, payload, accessToken);
        if (res?.status === 201) {
          showSuccessToast("Employee unassigned.");
        await fetchJobAssignments(jobId);
        await fetchJobDetail(jobId);
        await fetchJobs(1, false, debouncedJobSearchTerm, jobId);
        } else showErrorToast("Failed to unassign.");
      } else {
        // ASSIGN
        const payload = {
          UserId: loginUserId, ReturnRecordId: true, ReturnRecordError: true,
          SaveList: [{
            Id: 0,
            ClientEmployeeId: empId,
            ClientJobId: jobId, ModifiedBy: loginUserId
          }]
        };
        const res = await Postrequestcall(SAVE_CLIENT_EMPLOYEES_JOBS, payload, accessToken);
        if (res?.status === 201 || res?.status === 200) {
          showSuccessToast("Employee assigned.");
          await fetchJobAssignments(jobId);
          await fetchJobDetail(jobId);
          await fetchJobs(1, false, debouncedJobSearchTerm, jobId);
        } else showErrorToast("Failed to assign.");
      }
    } catch (e) {
      showErrorToast("Action failed.");
    } finally {
      setTogglingEmployeeId(null);
    }
  };

  /**
   * Adds a new activity to the job.
   * Uses the SAVE_CLIENT_JOB_ACTIVITIES endpoint.
   */
  const handleAddActivity = async () => {
    if (!activityForm.id) {
        showErrorToast("Activity ID is required.");
        return;
    }
    const jobId = getJobId(selectedJob);
    setActivitySaving(true);
    try {
      const payload = {
        UserId: loginUserId, ReturnRecordId: true, ReturnRecordError: true,
        SaveList: [{
          id: 0, deleted: false, clientId: clientId, clientJobId: jobId,
          clientLabourActivityId: Number(activityForm.id),
          modifiedBy: loginUserId, labourActivityName: activityForm.name || ""
        }]
      };
      const res = await Postrequestcall(SAVE_CLIENT_JOB_ACTIVITIES, payload, accessToken);
      if (res?.status === 200) {
        showSuccessToast("Activity added.");
        setActivityForm({ id: "", name: "" }); // Reset form
        fetchJobActivities(jobId); // Refresh list
      } else {
        showErrorToast("Unable to add activity.");
      }
    } catch (e) {
      showErrorToast("Error adding activity.");
    } finally {
      setActivitySaving(false);
    }
  };

  /**
   * Deletes/Disables an activity from the job.
   * Uses the DELETE_CLIENT_JOB_ACTIVITIES endpoint.
   */
  const handleDeleteActivity = async (activity) => {
    const jobId = getJobId(selectedJob);
    setActivityDeletingId(activity.id);
    try {
      const payload = {
        UserId: loginUserId, ReturnRecordId: true, ReturnRecordError: true, SoftDelete: true,
        DeleteList: [{ id: activity.id }]
      };
      const res = await Postrequestcall(DELETE_CLIENT_JOB_ACTIVITIES, payload, accessToken);
      if (res?.status === 200) {
        showSuccessToast("Activity disabled.");
        fetchJobActivities(jobId);
      }
    } catch (e) {
      showErrorToast("Error disabling.");
    } finally {
      setActivityDeletingId(null);
    }
  };

  // --- VARIABLES ---

  const jobCode = selectedJob?.clientJobCode || selectedJob?.jobCode;
  const jobName = selectedJob?.clientJobName || selectedJob?.name || selectedJob?.JobName;
  const jobActive =
    selectedJob?.jobActive ?? selectedJob?.clientJobActive ?? selectedJob?.Active ?? selectedJob?.active ?? false;
  const jobActivitiesCount =
    selectedJob?.noOfActivities ?? selectedJob?.NoOfActivities ?? activities.length;
  const jobEmployeesCount =
    selectedJob?.noOfEmployeesJobs ??
    selectedJob?.NoOfEmployeesJobs ??
    assignedEmployees.length;
  const companyName = selectedClientData?.OrganizationName || "N/A";

  const isTabContentLoading = activityLoading || assignmentLoading;

  return (
    <Box sx={{ height: "100%", display: "flex", overflow: "hidden", gap: 3 }}>
      {/* LEFT PANEL: JOB LIST */}
      <Paper
        elevation={0}
        sx={{
          width: { xs: "100%", md: 340 }, flexShrink: 0, borderRadius: 3, border: "1px solid #E0E0E0",
          display: "flex", flexDirection: "column", overflow: "hidden"
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: "1px solid #E0E0E0" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Jobs
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setAddJobOpen(true)}
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}>
              Add Job
            </Button>
          </Stack>
          <TextField
            fullWidth
            size="small"
            placeholder="Search jobs..."
            value={jobSearchTerm}
            onChange={(e) => setJobSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                setDebouncedJobSearchTerm(e.currentTarget.value.trim());
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9e9e9e", fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: jobSearchTerm && (
                <InputAdornment position="end">
                  <ClearIcon
                    sx={{ cursor: "pointer", fontSize: 18, color: "#9e9e9e" }}
                    onClick={() => {
                      setJobSearchTerm("");
                      setDebouncedJobSearchTerm("");
                    }}
                  />
                </InputAdornment>
              )
            }}
            sx={{ mt: 1, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#F5F7FA" } }}
          />
        </Box>

        {loadingJobs ? (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}><CircularProgress size={24} /></Stack>
        ) : (
          <>
            <List
              disablePadding
              sx={{ overflowY: "auto", flex: 1 }}
              ref={jobListRef}
              onScroll={handleJobListScroll}
            >
              {jobs.map((job) => {
                const jobId = getJobId(job);
                const isActive = job?.jobActive ?? job?.clientJobActive ?? job?.Active ?? job?.active;
                const isSelected = jobId === getJobId(selectedJob);
                return (
                  <ListItem
                    key={jobId}
                    sx={{
                      py: 1.5,
                      borderBottom: "1px solid #F0F0F0",
                      cursor: "pointer",
                      bgcolor: isSelected ? "rgba(33, 150, 243, 0.08)" : "transparent",
                      borderLeft: isSelected ? "4px solid #2196F3" : "4px solid transparent",
                      "&:hover": { bgcolor: "rgba(33, 150, 243, 0.05)" }
                    }}
                    onClick={() => handleSelectJob(job)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: isSelected ? "primary.main" : "#E0E0E0", color: isSelected ? "white" : "#757575" }}>
                        <WorkIcon sx={{ fontSize: 18 }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="subtitle2" sx={{ fontWeight: isSelected ? 700 : 600 }}>{job?.clientJobName || job?.name}</Typography>}
                      secondary={
                        <Box component="span" display="flex" alignItems="center" justifyContent="space-between" mt={0.5}>
                          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                            {job?.clientJobCode || job?.jobCode}
                          </Typography>
                          <Chip label={isActive ? "Active" : "Inactive"} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: isActive ? "#E8F5E9" : "#FFEBEE", color: isActive ? "#2E7D32" : "#C62828" }} />
                        </Box>
                      }
                      secondaryTypographyProps={{ component: "div" }}
                    />
                  </ListItem>
                );
              })}
            </List>
            {loadingMoreJobs && (
              <Box sx={{ py: 1.5, textAlign: "center" }}>
                <CircularProgress size={18} />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* RIGHT PANEL: JOB DETAILS */}
      <Paper
        elevation={0}
        sx={{
          flex: 1, borderRadius: 3, border: "1px solid #E0E0E0", display: "flex",
          flexDirection: "column", overflow: "hidden", position: "relative"
        }}
      >
        {jobDetailLoading && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.7)" }}>
            <CircularProgress />
          </Box>
        )}

        {selectedJob ? (
          <Stack sx={{ height: "100%" }}>
            {/* Header Info */}
            <Box sx={{ p: 3, borderBottom: "1px solid #E0E0E0", bgcolor: "#FAFAFA" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{jobName || "Untitled Job"}</Typography>
                  <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main", fontWeight: 600 }}>{jobCode || "N/A"}</Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: 'block' }}>{companyName}</Typography>
                </Box>
                <Chip label={jobActive ? "ACTIVE" : "INACTIVE"} sx={{ fontWeight: 700, bgcolor: jobActive ? "#E8F5E9" : "#FFEBEE", color: jobActive ? "#2E7D32" : "#C62828" }} />
              </Stack>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: "1px solid #E0E0E0", px: 3, bgcolor: 'white' }}>
              <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ minHeight: 48 }}>
                <Tab label="Details" sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }} />
                <Tab label="Activities" sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }} />
                <Tab label="Employees" sx={{ textTransform: "none", fontWeight: 600, minWidth: 100 }} />
              </Tabs>
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1, overflowY: "auto", p: 3, bgcolor: "#F8F9FA", position: "relative" }}>
              {isTabContentLoading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 5,
                    background: "rgba(255,255,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
              {/* DETAILS TAB */}
              {activeTab === 0 && (
                <Box>
                  <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <PlaceIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Location</Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {[selectedJob?.address1, selectedJob?.address2, selectedJob?.city, selectedJob?.province, selectedJob?.postalCode, selectedJob?.country].filter(Boolean).join(", ") || "No location specified"}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center"
                        }}>
                        <AssignmentIcon sx={{ fontSize: 32, color: "#BDBDBD", mb: 1 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "primary.main" }}>
                          {jobActivitiesCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Activities
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2.5,
                          borderRadius: 2,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center"
                        }}>
                        <GroupIcon sx={{ fontSize: 32, color: "#BDBDBD", mb: 1 }} />
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "primary.main" }}>
                          {jobEmployeesCount}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Employees
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ACTIVITIES TAB (UPDATED) */}
              {activeTab === 1 && (
                <Box>
                  {/* Full Width Search Bar */}
                  <TextField
                    size="small"
                    placeholder="Search activities..."
                    value={activitySearchTerm}
                    onChange={(e) => setActivitySearchTerm(e.target.value)}
                    fullWidth
                    sx={{ mb: 2, bgcolor: "white", borderRadius: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  
                  {activityLoading && activities.length === 0 ? (
                    <Stack alignItems="center" py={5}><CircularProgress size={24} /></Stack>
                  ) : (
                    <Paper
                      variant="outlined"
                      ref={activityListRef}
                      onScroll={handleActivityScroll}
                      sx={{ borderRadius: 2, overflow: "auto", maxHeight: 360 }}
                    >
                      <List disablePadding>
                        {activities.map((act) => (
                          <ListItem key={act.id} divider sx={{ bgcolor: "white" }}>
                            <ListItemText
                              primary={act?.labourActivityName || "N/A"}
                              secondary={act?.labourActivityCode}
                            />
                            <ListItemSecondaryAction>
                              <IconButton disabled={activityDeletingId === act.id} onClick={() => handleDeleteActivity(act)}>
                                <DeleteIcon color="error" fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                        {activities.length === 0 && (
                          <Box sx={{ p: 3, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                              No activities found
                            </Typography>
                          </Box>
                        )}
                      </List>
                      {loadingMoreActivities && (
                        <Box sx={{ py: 1.5, textAlign: "center" }}>
                          <CircularProgress size={18} />
                        </Box>
                      )}
                    </Paper>
                  )}
                </Box>
              )}

              {/* EMPLOYEES TAB */}
              {activeTab === 2 && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="Search employees..."
                      value={employeeSearchTerm}
                      onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                      sx={{ flex: 1, bgcolor: "white", borderRadius: 2 }}
                    />
                  </Stack>

                  {assignmentLoading && togglingEmployeeId === null ? (
                    <Stack alignItems="center" py={5}><CircularProgress size={24} /></Stack>
                  ) : (
                    <Paper
                      variant="outlined"
                      ref={assignmentListRef}
                      onScroll={handleAssignmentScroll}
                      sx={{ borderRadius: 2, overflow: "auto", maxHeight: 360 }}
                    >
                      <List disablePadding>
                        {assignedEmployees.map((emp) => {
                          const empId = emp?.clientEmployeeId || emp?.id;
                          const isAssigned = Boolean(emp?.isSelected);
                          const isLoading = togglingEmployeeId === empId;

                          return (
                            <ListItem
                              key={empId}
                              divider
                              sx={{ bgcolor: "white", opacity: isLoading ? 0.7 : 1 }}
                            secondaryAction={
                              <ToggleSwitch
                                edge="end"
                                checked={isAssigned}
                                disabled={isLoading}
                                onChange={() => handleToggleAssignment(emp)}
                                sx={{ mr: 1 }}
                              />
                            }
                            >
                              <ListItemIcon>
                                <Avatar
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    fontSize: 14,
                                    bgcolor: isAssigned ? "primary.main" : "#E0E0E0",
                                    color: isAssigned ? "white" : "#757575"
                                  }}
                                >
                                  {emp?.firstName?.[0]}
                                  {emp?.lastName?.[0]}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={`${emp?.firstName || ""} ${emp?.lastName || ""}`}
                                secondary={
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isAssigned ? "primary.main" : "text.secondary",
                                      fontWeight: isAssigned ? 600 : 400
                                    }}
                                  >
                                    {isAssigned ? jobCode : "Unassigned"}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          );
                        })}
                        {assignedEmployees.length === 0 && (
                          <Box sx={{ p: 3, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">
                              No employees found
                            </Typography>
                          </Box>
                        )}
                      </List>
                      {loadingMoreAssignments && (
                        <Box sx={{ py: 1.5, textAlign: "center" }}>
                          <CircularProgress size={18} />
                        </Box>
                      )}
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          </Stack>
        ) : (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <WorkIcon sx={{ fontSize: 60, mb: 2, color: "#E0E0E0" }} />
            <Typography>Select a job to view details</Typography>
          </Stack>
        )}
      </Paper>

      {/* ADD JOB DIALOG */}
      <Dialog
        open={addJobOpen}
        onClose={() => {
          setAddJobOpen(false);
          resetJobForm();
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Create New Job</DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField autoFocus label="Job Name" fullWidth required value={newJobForm.jobName} onChange={(e) => handleJobFormChange("jobName", e.target.value)} />
            <TextField label="Job Code" fullWidth required value={newJobForm.jobCode} onChange={(e) => handleJobFormChange("jobCode", e.target.value)} />
            <FormControlLabel control={<Switch checked={newJobForm.active} onChange={(e) => handleJobFormChange("active", e.target.checked)} />} label="Active" />
            
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Job Address
              </Typography>
              <SearchMap
                setSearchTerm={setAddressSearchTerm}
                searchTerm={addressSearchTerm}
                handleSearch={handleJobAddressSelection}
                placeholder="Search Address..."
                style={{ marginBottom: "10px" }}
              />
              <Stack spacing={2}>
                <TextField
                  label="Address 1"
                  placeholder="Street address"
                  fullWidth
                  size="small"
                  value={newJobForm.address1}
                  onChange={(e) => handleJobFormChange("address1", e.target.value)}
                />
                <TextField
                  label="Address 2"
                  placeholder="Apt, suite, etc."
                  fullWidth
                  size="small"
                  value={newJobForm.address2}
                  onChange={(e) => handleJobFormChange("address2", e.target.value)}
                />
                <TextField
                  label="Postal / Zip Code"
                  placeholder="Postal code"
                  fullWidth
                  size="small"
                  value={newJobForm.postalCode}
                  onChange={(e) => handleJobFormChange("postalCode", e.target.value)}
                />
                <TextField
                  label="Country"
                  placeholder="Country"
                  fullWidth
                  size="small"
                  value={newJobForm.country}
                  onChange={(e) => handleJobFormChange("country", e.target.value)}
                />
                <TextField
                  label="Province / State"
                  placeholder="Province/State"
                  fullWidth
                  size="small"
                  value={newJobForm.province}
                  onChange={(e) => handleJobFormChange("province", e.target.value)}
                />
                <TextField
                  label="City"
                  placeholder="City"
                  fullWidth
                  size="small"
                  value={newJobForm.city}
                  onChange={(e) => handleJobFormChange("city", e.target.value)}
                />
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => {
              setAddJobOpen(false);
              resetJobForm();
            }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateJob} disabled={savingJob}>
            {savingJob ? "Creating..." : "Create Job"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Jobs;
